import React, { ComponentProps, Fragment, useCallback, useMemo, useState } from "react";
import {
    AppId,
    capitalize,
    FCX,
    getFormattedThousand,
    ID,
    includesInLC,
    markMatch, ProductLogo,
    startsFromInLC,
    TextInput,
    Tooltip
} from "@datassential/platform-ui-lib";
import './MacsList/MacsList.scss';
import { AiOutlineSearch } from "react-icons/ai";
import { IMacItemObsolete, useMacsListObsolete } from "@apiHooks/useMacsListObsolete";
import Table from "@components/new/Table/Table";
import { iTableConfigItem } from "@components/new/Table/Table.helpers";
import useTableSort from "@components/new/Table/useTableSort";
import { GoPackage } from "react-icons/go";
import { BsDownload } from 'react-icons/bs';
import { IoShareSocial } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { addFileToPackage } from "@actions/packages.actions";
import { Link } from "react-router-dom";
import { routeNames } from "@core/routeNames";
import { sanitizeUrl } from "@helpers";
import http from "@core/fetch";
import SmallLoader from "@components/SmallLoader/SmallLoader";
import cn from "classnames";
import { setSharePopupState } from "@actions/documents.actions";
import { shareDataSelector } from "@selectors/documents.selectors";
import SharePopup from "@pages/_shared/SharePopup/SharePopup";
import RowDescriptionObsolete from "./RowDescriptionObsolete";
import Widget from "@components/Widget/Widget";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import { requestTrialByFeature } from "@actions/page";
import { PermissionLevelEnum } from "@models/PermissionSection";

function getMacItemSortingScore(item: IMacItemObsolete, query: string): number {
    const fields = [item.title, item.description.preview, item.description.additional];

    if (fields.some(field => startsFromInLC(field, query))) {
        return 100;
    } else if (fields.some(field => includesInLC(field, query))) {
        return 50;
    } else return 1;
}

const RowLink: FCX<{
    to?: ComponentProps<Link>['to'];
}> = (props) => {
    return (
        /* @ts-ignore */
        <Link
            {...props}
            to={props.to ?? ''}
        />
    );
};

const MacsListTableObsolete: FCX = () => {
    const dispatch = useDispatch();
    const isSharePopupOpen = useSelector(shareDataSelector);
    const [downloadingItems, setDownloadingItems] = useState<ID[]>([]);
    const [query, setQuery] = useState<string>('');
    const {
        data: { items: rawData, permissionLevel: macsPermissionLevel },
        isLoading,
    } = useMacsListObsolete();

    const isBlocked = macsPermissionLevel !== PermissionLevelEnum.Full;

    const requestTrial = useCallback(() => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('macs'));
    }, [dispatch]);

    const {
        sortedData,
        sortingDirection,
        sortingField,
        getColumnIconNoDefault,
        handleSort,
    } = useTableSort(
        rawData,
        'dateSort',
        'desc',
        {
            title: 'asc',
            description: 'asc',
            itemsCount: 'desc',
            dateSort: 'desc',
        }
    );

    const data = useMemo(
        () => {
            if (query.trim() !== '') {
                const result = sortedData.filter((item) => {
                    const fields = [item.title, item.description.preview, item.description.additional];
                    return fields.some(field => includesInLC(field, query));
                });

                result.sort((a, b) => {
                    const aScore = getMacItemSortingScore(a, query);
                    const bScore = getMacItemSortingScore(b, query);

                    return bScore - aScore;
                });

                return result;
            }

            return [...sortedData];
        },
        [sortedData, query],
    );

    const share = useCallback(
        (item: IMacItemObsolete) => {
            dispatch(setSharePopupState({
                id: item.id,
                documentType: item.documentType,
                shareLinkString: item.shareLink,
                publicationFamily: item.publicationFamily,
                title: item.title,
                popupType: 'allDocuments'
            }));
        },
        [dispatch]
    );

    const addToPackage = useCallback((id: ID) => {
        toast.success('Item has been added to package');
        dispatch(addFileToPackage({
            itemId: id,
            itemDocumentType: 'mac',
        }));
    }, [dispatch]);

    const downloadFile = useCallback(
        async (url: string, itemId: ID) => {
            setDownloadingItems((prev) => [...prev, itemId]);
            try {
                await http.downloadFileXHRFromUrl('get', url);
                toast.success('File has been downloaded');
            } catch (e) {
                toast.error('Error while downloading file');
            }
            setDownloadingItems((prev) => prev.filter((id) => id !== itemId));
        },
        []
    );

    const tableConfig = useMemo(
        (): iTableConfigItem[] => {
            return [
                {
                    title: 'Country',
                    icon: getColumnIconNoDefault('countryCodeStr', sortingField, sortingDirection),
                    handleHeaderClick: handleSort('countryCodeStr'),
                    styles: {
                        cell: {
                            flex: '0 0 80px',
                            paddingLeft: 0,
                        },
                    },
                    Value: ({ index }) => <>{data[index]?.countryCodeStr}</>,
                },
                {
                    title: 'Name',
                    icon: getColumnIconNoDefault('title', sortingField, sortingDirection),
                    handleHeaderClick: handleSort('title'),
                    styles: {
                        cell: {
                            flex: '2',
                        },
                    },
                    getHtmlAttributes: (index, isHeader): Record<string, string> => {
                        if (!isHeader) {
                            return {
                                'data-tooltip-id': `macs-list-table-item-thumbnail-${data[index]?.id}`,
                            };
                        }

                        return {};
                    },
                    Value: ({ index }) => (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: query.trim() === ''
                                    ? data[index]?.title
                                    : markMatch(data[index]?.title, query.trim(), true)
                            }}
                        />
                    ),
                },
                {
                    title: 'Description',
                    icon: getColumnIconNoDefault('description', sortingField, sortingDirection),
                    handleHeaderClick: handleSort('description'),
                    Value: ({ index }) => <RowDescriptionObsolete query={query} row={data[index]}/>,
                    styles: {
                        cell: {
                            flex: '2.5',
                        },
                    },
                },
                {
                    title: 'Items Count',
                    Value: ({ index }) => <>{getFormattedThousand(data[index]?.itemsCount)}</>,
                    icon: getColumnIconNoDefault('itemsCount', sortingField, sortingDirection),
                    handleHeaderClick: handleSort('itemsCount'),
                    styles: {
                        cell: {
                            flex: '0 0 120px',
                            justifyContent: 'center',
                            textAlign: 'center',
                        },
                    },
                },
                {
                    title: 'Updated Date',
                    Value: ({ index }) => <>{data[index]?.dateStr}</>,
                    handleHeaderClick: handleSort('dateSort'),
                    icon: getColumnIconNoDefault('dateSort', sortingField, sortingDirection),
                    styles: {
                        cell: {
                            flex: '0 0 150px',
                            justifyContent: 'center',
                            textAlign: 'center',
                        },
                    },
                },
                {
                    title: 'Actions',
                    Value: ({ index }) => {
                        const item = data[index];
                        return (
                            <div
                                className="MacsListTable__actions"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                }}
                            >
                                <div
                                    className={cn(
                                        "MacsListTable__action",
                                        downloadingItems.includes(item.id) && 'is-disabled'
                                    )}
                                    title="Download"
                                    data-testid={`buttonMacsDownload-${index}`}
                                >
                                    {downloadingItems.includes(item.id) ? (
                                        <SmallLoader className="MacsListTable__action-loader"/>
                                    ) : (<>
                                        <BsDownload data-tooltip-id={`macs-list-table-item-download-${item?.id}`}/>
                                    </>)}
                                </div>
                                <GoPackage
                                    className="MacsListTable__action is-clickable"
                                    onClick={() => {
                                        addToPackage(item?.id);
                                    }}
                                    title="Add to package"
                                    data-testid={`buttonMacsAddToPackage-${index}`}
                                />
                                <IoShareSocial
                                    title="Share"
                                    className="MacsListTable__action is-clickable"
                                    onClick={() => share(item)}
                                    data-testid={`buttonMacsShare-${index}`}
                                />
                            </div>
                        );
                    },
                    styles: {
                        cell: {
                            flex: '0 0 150px',
                            paddingLeft: 0,
                            justifyContent: 'center',
                        },
                    },
                },
            ];
        },
        [
            data,
            getColumnIconNoDefault,
            handleSort,
            sortingField,
            sortingDirection,
            query,
            addToPackage,
            downloadingItems,
            share
        ],
    );

    return (
        <Widget className={cn("MacsListTable", isBlocked && "is-blocked")} testId="blockMacsListTable">
            <div className="MacsListTable__wrapper">
                <div className="MacsListTable__table">
                    {!isBlocked && (
                        <div className="MacsListTable__table-search">
                            <TextInput
                                value={query}
                                setValue={setQuery}
                                placeholder="Search for a specific MAC"
                                className="MacsListTable__search"
                                testId="inputMacsSearch"
                            />
                            <AiOutlineSearch className="MacsListTable__search-icon"/>
                        </div>
                    )}
                    {isLoading && (
                        <div className="MacsListTable__loading-overlay">
                            <div className="TrendingNow__spinner">
                                <ProductLogo appId={AppId.Snap} type="regular"/>
                            </div>
                        </div>
                    )}
                    <Table
                        isCopyButtonHidden
                        config={tableConfig}
                        rowsCount={data.length}
                        theme="light"
                        RowComponent={RowLink}
                        testId={"tableMacs"}
                        getRowProps={(index) => ({
                            to: {
                                pathname: routeNames.viewDocument,
                                search: `?id=${data[index]?.id}&doctype=${data[index]?.documentType}`
                            }
                        })}
                    />
                    {data.map((item, index) => (
                        <Fragment key={item.id}>
                            <Tooltip
                                id={`macs-list-table-item-thumbnail-${item.id}`}
                                place="right"
                                style={{
                                    fontSize: 0,
                                    lineHeight: 0,
                                }}
                                delayShow={200}
                            >
                                <div
                                    className="MacsListTable__thumbnail"
                                    data-testid={`buttonMacsThumbnail-${index}`}
                                >
                                    <img src={item.thumbnailUrl} alt="thumbnail"/>
                                </div>
                            </Tooltip>
                            <Tooltip
                                id={`macs-list-table-item-description-${item.id}`}
                                place="right"
                                style={{ maxWidth: 400, zIndex: 1000 }}
                            >
                                <div
                                    className="MacsListTable__description-tooltip"
                                    dangerouslySetInnerHTML={{
                                        __html: query.trim() === ''
                                            ? data[index]?.description.additional
                                            : markMatch(data[index]?.description.additional, query.trim(), true)
                                    }}
                                />
                            </Tooltip>
                            <Tooltip
                                id={`macs-list-table-item-download-${item.id}`}
                                place="left"
                                clickable
                            >
                                <div className="MacsListTable__download-links">
                                    {item.downloadLinks.map((link) => (
                                        <div
                                            key={link.fileExtension}
                                            onClick={() => downloadFile(
                                                sanitizeUrl(`${process.env.REACT_APP_API}/${link.url}`),
                                                item.id,
                                            )}
                                            className="MacsListTable__download-link"
                                            data-testid={`buttonMacsDownloadLink${capitalize(link.fileExtension)}-${index}`}
                                        >
                                            {link.fileExtension}
                                        </div>
                                    ))}
                                </div>
                            </Tooltip>
                        </Fragment>
                    ))}
                </div>
                {/* @ts-ignore */}
                {isSharePopupOpen && <SharePopup/>}
            </div>
            {isBlocked && (
                <PermissionOverlay
                    onContactUsClick={requestTrial}
                    testId="blockMacsPermissionOverlay"
                >
                    <div>
                        Get the full Datassential One experience -
                        upgrade now to access the Menu Adoption Cycle Reports
                    </div>
                </PermissionOverlay>
            )}
        </Widget>
    );
};

export default MacsListTableObsolete;